import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";

const CustomDataGrid = ({
  columnsObject,
  dataRows,
  sortField,
  sortOrder,
  pageSize = 50,
  height = 500,
  dynamicRowHeightField = "",
  columnPercentage = 0.5,
  getRowId = (row) => row.id,
  checkboxSelection = false,
  onSelectionModelChange = null,
  fixedHeight = 30,
}) => {
  // Si se pasa un campo de ordenamiento (sortField) y el orden (sortOrder), ordenamos los datos
  let sortedData = [...dataRows];

  if (sortField && sortOrder) {
    sortedData = sortedData.sort((a, b) => {
      if (sortOrder === "asc") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    });
  }

  // Modificar las columnas para que cada una reciba una propiedad `textTransform`
  const modifiedColumns = columnsObject.map((col) => {
    // Si la columna ya tiene un renderCell, no lo modificamos
    if (col.renderCell) {
      return col;
    }

    return {
      ...col,
      renderCell: (params) => {
        let text = params.value;

        if (!text) {
          text = ""; // O puedes asignar otro valor predeterminado si lo prefieres
        }

        // Aplicar transformación de texto según `textTransform`
        if (col.textTransform) {
          switch (col.textTransform) {
            case "uppercase":
              text = text.toUpperCase();
              break;
            case "lowercase":
              text = text.toLowerCase();
              break;
            case "capitalize":
              // Capitaliza la primera letra de cada palabra
              text = text
                .toLowerCase() // Asegura que todo el texto esté en minúsculas primero
                .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitaliza la primera letra de cada palabra
              break;
            default:
              break;
          }
        }
        // Verificamos si la columna tiene `wrap: true`, para aplicar los estilos
        const hasWrap = col.wrap === true;
        const wrapLength = col.wrapLength || 25; // Longitud de envoltura por defecto

        // Si no hay `wrap` y el texto es largo, truncamos el texto
        if (!hasWrap && text.length > wrapLength) {
          // Limita los caracteres (puedes ajustar el valor 30)
          text = text.slice(0, wrapLength) + "..."; // Truncamos y añadimos "..."
        }

        return (
          <div
            style={{
              whiteSpace: hasWrap ? "normal" : "nowrap", // Asegura que el texto se pueda envolver solo si wrap es true
              wordWrap: hasWrap ? "break-word" : "normal", // Asegura que las palabras largas se rompan solo si 'wrap' es true
              overflow: !hasWrap ? "hidden" : "visible", // Oculta el desbordamiento si no hay wrap
              textOverflow: !hasWrap ? "ellipsis" : "clip", // Añade "..." si el texto está truncado
              display: "block", // Asegura que el texto se muestre correctamente
            }}
          >
            {text}
          </div>
        );
      },
    };
  });

  // Estilo de filas alternadas
  const rowStyles = {
    // Estilo para las filas intercaladas
    "& .MuiDataGrid-row:nth-of-type(odd)": {
      backgroundColor: "#e8eaf6", // color para filas impares
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
      backgroundColor: "#fafafa", // color para filas pares
    },
    // Estilo para el encabezado de la tabla
    "& .MuiDataGrid-columnHeaderTitle": {
      padding: "10px 12px", // Más padding en el encabezado para hacerlo más ancho
    },
    // Header más bajo
    "& .MuiDataGrid-columnHeaders": {
      height: 28,
      minHeight: 28,
      maxHeight: 28,
      borderBottom: "1px solid #ccc",
    },
    "& .MuiDataGrid-columnHeader": {
      paddingTop: 0,
      paddingBottom: 0,
      fontSize: "0.72rem",
      lineHeight: "1.1rem",
    },
  };

  // Función para calcular dinámicamente el alto de las filas basado en el contenido
  const calculateRowHeight = (row) => {
    const baseLineHeight = fixedHeight; // Altura base por línea
    const charWidth = 8; // Ancho promedio de un carácter

    // Si no se definió el campo dinámico, devolver altura base
    if (!dynamicRowHeightField) return baseLineHeight;

    // Ancho de la columna en px
    const columnWidth = window.innerWidth * columnPercentage;

    // Estimar cuántos caracteres caben por línea
    const charsPerLine = Math.floor(columnWidth / charWidth) || 1;

    const fieldText = row[dynamicRowHeightField] || "";
    const textLength = fieldText.length;

    // Calcular líneas necesarias y retornar altura
    const linesNeeded = Math.ceil(textLength / charsPerLine);

    return baseLineHeight * Math.max(1, linesNeeded);
  };

  return (
    <Box sx={{ height: height, width: "100%", overflowY: "auto" }}>
      <DataGrid
        rows={sortedData}
        columns={modifiedColumns}
        pageSize={pageSize}
        rowsPerPageOptions={[pageSize]}
        getRowHeight={(params) => calculateRowHeight(params.model)} // Calcula la altura de la fila
        sx={rowStyles} // Estilos para las filas alternadas
        getRowId={getRowId}
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={onSelectionModelChange}
        headerHeight={36}
      />
    </Box>
  );
};

export default CustomDataGrid;
